import { useHistory, useLocation } from 'react-router-dom';
import {
  fromPairs as _fromPairs,
  isArray as _isArray,
  isEqual as _isEqual,
  isNumber as _isNumber,
} from 'lodash';

type ParamSetter<T> = (newParams: T) => void;

export function useQueryParams<
  T extends { [k: string]: string | number | string[] }
>(defaultParams: T): [T, ParamSetter<T>] {
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const params = _fromPairs(
    Object.entries(defaultParams).map(([key, defaultValue]) => {
      if (_isArray(defaultValue)) {
        const values = searchParams.getAll(key).filter((param) => param !== '');
        return [key, values.length === 0 ? defaultValue : values];
      }

      const value = searchParams.get(key) ?? defaultValue;
      const typedValue = _isNumber(defaultValue) ? Number(value) : value;

      return [key, typedValue];
    })
  );

  const setParams: ParamSetter<T> = (newParams) => {
    const nonDefaultParamEntries = Object.entries(newParams)
      .filter(([key, value]) => !_isEqual(value, defaultParams[key]))
      .flatMap(([key, value]) =>
        _isArray(value) ? value.map((v) => [key, v]) : [[key, String(value)]]
      );
    const newSearchParamsString = new URLSearchParams(
      nonDefaultParamEntries
    ).toString();
    const path =
      newSearchParamsString.length > 0
        ? `${location.pathname}?${newSearchParamsString}`
        : location.pathname;
    history.replace(path);
  };

  return [params as T, setParams];
}
